import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { DashboardSection } from "@/components/dashboard-section";
import { HeroStatusStrip } from "@/components/hero-status-strip";
import { LivePricing } from "@/components/live-pricing";
import { CompoundPreview } from "@/components/compound-preview";
import { AddAccountButton } from "@/components/add-account/add-account-button";
import { AccountPairCard } from "@/components/account-pair-card";
import { StatRow } from "@/components/stat-card";
import { CoolIcon } from "@/components/ui/cool-icon";
import { Loader2 } from "@/components/ui/icons";
import {
  listAccounts,
  decodeAccountInfo,
  type AccountRow,
} from "@/lib/api/accounts";
import { listAccountPairs, type AccountPairListItem } from "@/lib/api/account-pairs";
import { listDecisions, type DecisionRow } from "@/lib/api/decisions";
import { useDashboardStore } from "@/store/dashboard.store";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";

/**
 * Overview — operator landing. Business-aligned layout:
 *
 *   1. PageHeader
 *   2. HeroStatusStrip (trading window + live countdown)
 *   3. Stat row (balance / equity / active / failed)
 *   4. Active account pairs (top 4, DD bars = heartbeat per brand rule)
 *   5. Performance history — honest "Coming Soon" (no fabricated data)
 *   6. Compound preview (links to /compound)
 *   7. Recent decisions (last 5 Haiku calls)
 *   8. Alerts summary (from store)
 *
 * Everything here is driven by real APIs. When a data source doesn't
 * exist yet (equity curve, trades), we render a truthful empty card
 * rather than placeholder numbers.
 */

function fmtMoney(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface DerivedStats {
  totalBalance: number;
  totalEquity: number;
  activeCount: number;
  liveOnlineCount: number;
  pendingCount: number;
  failedCount: number;
}

function deriveStats(
  accounts: AccountRow[],
  liveAccounts: Record<
    string,
    {
      balance: number;
      equity: number;
      online: boolean | null;
    }
  >,
): DerivedStats {
  let totalBalance = 0;
  let totalEquity = 0;
  let activeCount = 0;
  let liveOnlineCount = 0;
  let pendingCount = 0;
  let failedCount = 0;
  for (const a of accounts) {
    const live = liveAccounts[a.id];
    const info = decodeAccountInfo(a);
    totalBalance += live?.balance ?? info?.balance ?? 0;
    totalEquity += live?.equity ?? info?.balance ?? 0;
    if (a.status === "active") activeCount++;
    else if (a.status === "pending" || a.status === "validating") pendingCount++;
    else if (a.status === "failed_validation") failedCount++;
    if (live?.online === true) liveOnlineCount++;
  }
  return {
    totalBalance,
    totalEquity,
    activeCount,
    liveOnlineCount,
    pendingCount,
    failedCount,
  };
}

function relTime(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

const DECISION_TONE: Record<string, string> = {
  continue: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  hold: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  alert: "bg-destructive/10 text-destructive",
  execute: "bg-accent/10 text-accent",
  open_trade: "bg-accent/10 text-accent",
  close_pair: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  news_protect: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  news_restore: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  done_for_day: "bg-muted-foreground/10 text-muted-foreground",
};

function PairSubscriber({ pairId }: { pairId: string | null | undefined }): ReactNode {
  useHedgeSocket(pairId ?? null);
  return null;
}

export function OverviewPage(): ReactNode {
  const [accounts, setAccounts] = useState<AccountRow[] | null>(null);
  const [pairs, setPairs] = useState<AccountPairListItem[] | null>(null);
  const [decisions, setDecisions] = useState<DecisionRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const unreadAlertCount = useDashboardStore((s) => s.unreadAlertCount);
  const pairBuckets = useDashboardStore((s) => s.pairs);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([listAccounts(), listAccountPairs(), listDecisions()])
      .then(([a, p, d]) => {
        if (!alive) return;
        if (a.status === "fulfilled") setAccounts(a.value);
        else {
          setAccounts([]);
          setLoadError(
            a.reason instanceof Error ? a.reason.message : String(a.reason),
          );
        }
        setPairs(p.status === "fulfilled" ? p.value : []);
        setDecisions(d.status === "fulfilled" ? d.value.slice(0, 5) : []);
      });
    return () => {
      alive = false;
    };
  }, []);

  const liveAccounts = useMemo(() => {
    const out: Record<
      string,
      { balance: number; equity: number; online: boolean | null; updatedAt: number }
    > = {};
    for (const accountPair of pairs ?? []) {
      const pairId = accountPair.firstSymbolPairId;
      if (!pairId) continue;
      const live = pairBuckets[pairId];
      if (!live) continue;
      for (const accountId of [accountPair.longAccount.accountId, accountPair.shortAccount.accountId]) {
        const snapshot = live.accounts[accountId];
        if (!snapshot) continue;
        const prev = out[accountId];
        if (!prev || snapshot.updatedAt >= prev.updatedAt) {
          out[accountId] = {
            balance: snapshot.balance,
            equity: snapshot.equity,
            online: snapshot.online,
            updatedAt: snapshot.updatedAt,
          };
        }
      }
    }
    return out;
  }, [pairBuckets, pairs]);

  const stats = useMemo(
    () => (accounts ? deriveStats(accounts, liveAccounts) : null),
    [accounts, liveAccounts],
  );

  // ── loading ─────────────────────────────────────────────
  if (accounts === null) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-6">
        <PageHeader
          title="Overview"
          subtitle="Loading Your Fleet…"
          icon={<CoolIcon name="More_Grid_Big" />}
          accentWords={["Overview"]}
        />
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="mb-3 animate-spin text-accent" size={20} />
            <p className="text-sm text-muted-foreground">Loading Overview…</p>
          </div>
        </DashboardSection>
      </div>
    );
  }

  // ── error ───────────────────────────────────────────────
  if (loadError && accounts.length === 0) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-6">
        <PageHeader
          title="Overview"
          subtitle="Couldn't Load Your Fleet"
          icon={<CoolIcon name="Triangle_Warning" />}
          accentWords={["Overview"]}
        />
        <DashboardSection>
          <p className="py-8 text-center text-sm text-destructive">
            {loadError}
          </p>
        </DashboardSection>
      </div>
    );
  }

  // ── empty (no accounts) ─────────────────────────────────
  if (accounts.length === 0) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-6">
        <PageHeader
          title="Overview"
          subtitle="Add Your First Prop-Firm Account To Activate The Dashboard"
          icon={<CoolIcon name="More_Grid_Big" />}
          accentWords={["First"]}
          right={<AddAccountButton label="Add Account" />}
        />
        <DashboardSection>
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-2xl text-accent">
              <CoolIcon name="Credit_Card_02" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Nothing To Show Yet
              </h2>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Connect a prop-firm account to see its batch funnel, hedge
                pairs, and live equity here. Validation is read-only — we log
                in once and verify the account works.
              </p>
            </div>
            <AddAccountButton label="Add Your First Account" />
          </div>
        </DashboardSection>
      </div>
    );
  }

  // ── populated ───────────────────────────────────────────
  const topPairs = (pairs ?? []).slice(0, 4);
  const subscribedTailPairs = (pairs ?? []).slice(4);
  const subtitleCopy = (() => {
    const n = accounts.length;
    const active = stats!.activeCount;
    return `${n} Account${n === 1 ? "" : "s"} · ${active} Active · ${topPairs.length} Hedge Pair${topPairs.length === 1 ? "" : "s"}`;
  })();

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <PageHeader
        title="Overview"
        subtitle={subtitleCopy}
        icon={<CoolIcon name="More_Grid_Big" />}
        accentWords={["Overview"]}
        right={<AddAccountButton label="Add Account" />}
      />

      <HeroStatusStrip />

      <LivePricing />

      <div className="hidden">
        {subscribedTailPairs.map((pair) => (
          <PairSubscriber key={pair.id} pairId={pair.firstSymbolPairId} />
        ))}
      </div>

      <DashboardSection>
        <StatRow
          items={[
            { label: "Total Balance", value: fmtMoney(stats!.totalBalance) },
            { label: "Total Equity", value: fmtMoney(stats!.totalEquity) },
            {
              label: "Active Accounts",
              value: `${stats!.activeCount}/${accounts.length}`,
              valueClassName:
                stats!.activeCount === accounts.length
                  ? "text-emerald-500"
                  : "text-amber-500",
            },
            {
              label: "Live Accounts",
              value: `${stats!.liveOnlineCount}/${stats!.activeCount || accounts.length}`,
              valueClassName:
                stats!.liveOnlineCount > 0 ? "text-emerald-500" : "text-muted-foreground",
            },
          ]}
        />
      </DashboardSection>

      {topPairs.length > 0 && (
        <DashboardSection
          title="Active Hedge Pairs"
          titleRight={
            pairs && pairs.length > 4 ? (
              <Link
                to="/pairs"
                className="text-xs font-medium text-accent hover:underline"
              >
                View All ({pairs.length}) →
              </Link>
            ) : null
          }
        >
          <div className="grid gap-4 auto-rows-fr md:grid-cols-2">
            {topPairs.map((pair, i) => (
              <AccountPairCard key={pair.id} pair={pair} index={i} />
            ))}
          </div>
        </DashboardSection>
      )}

      <DashboardSection title="Performance History">
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-2xl text-accent">
            <CoolIcon name="Chart_Line" />
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight">
              Coming Soon
            </h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Once the bot closes its first trade, this section lights up with
              your equity curve, trade history, and trading calendar. Every
              chart on this page is real or absent — no placeholder numbers.
            </p>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CoolIcon name="Chart_Line" /> Equity Curve
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CoolIcon name="Chart_Bar_Vertical_01" /> Trade History
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CoolIcon name="Calendar" /> Trading Calendar
            </span>
          </div>
        </div>
      </DashboardSection>

      <CompoundPreview />

      {decisions && decisions.length > 0 && (
        <DashboardSection
          title="Recent Decisions"
          titleRight={
            <Link
              to="/decisions"
              className="text-xs font-medium text-accent hover:underline"
            >
              View All →
            </Link>
          }
        >
          <ul className="divide-y divide-border/40">
            {decisions.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between gap-3 py-3 text-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      DECISION_TONE[d.claude_action] ??
                      "bg-muted text-muted-foreground"
                    }`}
                  >
                    {d.claude_action}
                  </span>
                  <span className="truncate text-foreground">{d.reason}</span>
                </div>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  {relTime(d.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </DashboardSection>
      )}

      {unreadAlertCount > 0 && (
        <DashboardSection
          title="Alerts"
          titleRight={
            <Link
              to="/alerts"
              className="text-xs font-medium text-accent hover:underline"
            >
              View All →
            </Link>
          }
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <CoolIcon name="Bell_Ring" />
            </div>
            <p className="text-sm">
              <span className="font-semibold">
                {unreadAlertCount} Unread Alert
                {unreadAlertCount === 1 ? "" : "s"}
              </span>
              <span className="text-muted-foreground">
                {" "}
                — Open The Alerts Tab To Review.
              </span>
            </p>
          </div>
        </DashboardSection>
      )}
    </div>
  );
}
